import { useEffect, useRef, type ReactNode } from 'react'
import styles from './RangoCheckbox.module.css'

type Props = {
  /** Texto de apoio (`Web/Label MD 1`) — sempre visível. */
  label: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  /** Estado misto (indeterminate) — botão de "selecionar tudo". */
  indeterminate?: boolean
  disabled?: boolean
  id?: string
  name?: string
  error?: ReactNode
  className?: string
}

/**
 * Checkbox Rango DS — área de toque mínima `$size-6x` (48px), quadrado
 * com `$border-radius-xs`, seleção na cor primária.
 * Usa o `<input type="checkbox">` nativo (teclado e leitor de tela grátis).
 */
export function RangoCheckbox({
  label,
  checked,
  onChange,
  indeterminate = false,
  disabled = false,
  id,
  name,
  error,
  className,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <label className={styles.label} data-disabled={disabled ? 'true' : undefined}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="checkbox"
          className={styles.input}
          checked={checked}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.checked)}
          data-testid="rango-checkbox-input"
        />
        <span className={styles.box} aria-hidden="true">
          {indeterminate ? (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
              <path
                d="M6 12h12"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
              <path
                d="M5 12.5l4.5 4.5L19 7.5"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span className={styles.text}>{label}</span>
      </label>
      {error ? (
        <p className={styles.checkboxError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
